import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { readNetworkIdsFromParam } from '@/utils/routeUtils';
import { MAX_NETWORK_IDS } from '@/utils/constants';
import type { Network } from '@/types';

/**
 * Result of {@link useSelectedNetworks}.
 */
export interface UseSelectedNetworksResult {
  /** Network ids parsed from `?networks=`, in URL order, deduped and capped at {@link MAX_NETWORK_IDS}. */
  selectedNetworkIds: string[];
  /**
   * Replaces the selection by writing `?networks=` (other query params are preserved, history is
   * replaced). The incoming ids are validated against authorized networks, deduped and capped at
   * {@link MAX_NETWORK_IDS}; a change that would clear the selection is ignored (min 1 enforced).
   */
  setSelectedNetworkIds: (networkIds: string[]) => void;
  /** `true` while fewer than {@link MAX_NETWORK_IDS} networks are selected. */
  canSelectMore: boolean;
}

/**
 * Reads and writes the multi-network content performance selection via the `?networks=` query param
 * (comma-separated network ids).
 * The selection is capped at {@link MAX_NETWORK_IDS} (matching the API cap).
 *
 * Only the query-param content-performance mode reads/writes `?networks=`. The mode branching lives
 * here (rather than the caller passing `undefined` networks) so consumers don't special-case it: in
 * path mode the selection is always empty, writes are no-ops, and the cap is a single network.
 * @param networks - The user's authorized networks for the current app type.
 * @param isQueryMode - Whether the `?networks=` query-param mode is active. When `false`, this hook
 * short-circuits to a path-mode no-op.
 * @returns The current selection plus a setter and capacity flag.
 */
const useSelectedNetworks = (
  networks: Network[] | undefined,
  isQueryMode: boolean
): UseSelectedNetworksResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const networksParam = searchParams.get('networks');

  const selectedNetworkIds = useMemo(
    () => (isQueryMode ? readNetworkIdsFromParam(networksParam) : []),
    [isQueryMode, networksParam]
  );

  const setSelectedNetworkIds = useCallback(
    (networkIds: string[]) => {
      if (!isQueryMode || !networks || networks.length === 0) return;

      // Enforce max: dedupe (preserving order) then cap at the API limit.
      const nextIds = [...new Set(networkIds)].slice(0, MAX_NETWORK_IDS);
      // Enforce min 1: never let the selection be cleared (e.g. deselecting the last network).
      if (nextIds.length === 0) return;

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('networks', nextIds.sort().join(','));
          return next;
        },
        { replace: true }
      );
    },
    [isQueryMode, networks, setSearchParams]
  );

  // Path mode allows a single network (the multi-select is hidden); query mode allows up to the cap.
  const maxSelectableNetworks = isQueryMode ? MAX_NETWORK_IDS : 1;

  return {
    selectedNetworkIds,
    setSelectedNetworkIds,
    canSelectMore: selectedNetworkIds.length < maxSelectableNetworks,
  };
};

export default useSelectedNetworks;
